import axiosInstance from "../../interceptor";

export type OrgOption = {
  organizationId: number;
  organizationName: string;
  partnerId?: number | null;
};

export interface SearchOrganizationsResponse {
  status: "success" | "error";
  data?: OrgOption[];
  message?: string;
}

export const searchOrganizations = async (
  q: string,
  limit = 5,
): Promise<SearchOrganizationsResponse> => {
  try {
    // NOTE: baseURL already set in axiosInstance
    const data: any = await axiosInstance.get(
      `/coordinator/organizations?q=${encodeURIComponent(q)}&limit=${limit}`,
    );

    return {
      status: "success",
      data: Array.isArray(data?.data) ? data.data : [],
    };
  } catch (err: any) {
    return {
      status: "error",
      message:
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Something went wrong. Please try again later.",
    };
  }
};
export type SourceOfInformation = "Accounting records" | "Manual studies";

export type SummaryMetricsCreateRequest = {
  date?: string;
  sourceType: "accounting" | "manual";
  quarter: number;
  year: number;
  organizationId: number;
  partnerName: string;
  category: "offsetting" | "emissions";
  sub_category: string;
  invested_in_nature: number;
  total_emmission_till_now: number;
  compensated?: number;
  plan: "null" | "seed" | "tree" | "forest" | "landscape";
  type_of_partner: "partner" | "impact_partner";
  landscape_id: number;
};

export interface SubmitSummaryMetricsResponse {
  status: "success" | "error";
  message: string;
  data?: any;
}
export const submitSummaryMetrics = async (
  payload: SummaryMetricsCreateRequest,
): Promise<SubmitSummaryMetricsResponse> => {
  try {
    const data: any = await axiosInstance.post(
      `/coordinator/summary-metrics`,
      payload,
    );
    return { status: "success", message: "Submitted successfully", data };
  } catch (err: any) {
    return {
      status: "error",
      message:
        err?.response?.data?.errors ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Something went wrong. Please try again later.",
    };
  }
};

// utils.ts
export type PartnerDataResponse = {
  organization: { organizationId: number; organizationName: string };
  partner: {
    accountId: number;
    userId: number;
    account: {
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      phoneNumber: string | null;
      profilePicture: string | null;
    } | null;
    stripe: { stripeId: string | null; currency: string | null } | null;
    rankings: {
      overall: { invested_sum: string; rank: number } | null;
      period: { invested_sum: string; rank: number } | null;
    };
  };
  certificates: Array<{
    certificate_url: string | null;
    certificate_code: string | null;
    amount_paid: string | number | null;
    issuance_date: string | null;
  }>;
  summaryMetrics: {
    rows: any[];
    monthly: Array<{
      month: string; // yyyy-mm-01
      invested_sum: string | number;
      emission_sum: string | number;
    }>;
  };
};

export const getPartnerData = async (
  organizationId: number,
  fromDate?: string,
  toDate?: string,
) => {
  try {
    const res: any = await axiosInstance.get(`/coordinator/partner-data`, {
      params: {
        organizationId,
        fromDate,
        toDate,
      },
    });

    const payload = res?.data ?? res;

    return { status: "success", data: payload };
  } catch (err: any) {
    console.error("getPartnerData error =>", err);
    return {
      status: "error",
      message:
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to fetch partner data",
    };
  }
};
export type OrganizationDetails = {
  organizationId: number;
  organizationName: string;
  sector: string | null;
};

export type GetOrganizationByIdResponse =
  | { status: "success"; data: OrganizationDetails }
  | { status: "error"; message: string };

export type UpdateOrganizationSectorResponse =
  | {
      status: "success";
      data: { organizationId: number; sector: string | null };
    }
  | { status: "error"; message: string };

export const getOrganizationById = async (
  organizationId: number,
): Promise<GetOrganizationByIdResponse> => {
  try {
    const res: any = await axiosInstance.get(
      `/coordinator/organizations/${organizationId}`,
    );

    const payload = res?.data?.data ?? res?.data ?? res;
    return { status: "success", data: payload };
  } catch (err: any) {
    return {
      status: "error",
      message:
        err?.response?.data?.errors ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to fetch organization details",
    };
  }
};

export const updateOrganizationSector = async (
  organizationId: number,
  sector: string,
): Promise<UpdateOrganizationSectorResponse> => {
  try {
    const res: any = await axiosInstance.patch(
      `/coordinator/organizations/${organizationId}/sector`,
      { sector },
    );

    const payload = res?.data ?? res;

    return { status: "success", data: payload.data };
  } catch (err: any) {
    return {
      status: "error",
      message:
        err?.response?.data?.errors ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to update sector",
    };
  }
};

export type SustainabilityRoadmapAreaPayload = {
  priority_key: string;
  sort_order: number;
  baseline_value: number;
  target_value: number;
  current_value: number;
  secondary_label?: string | null;
  secondary_value?: number | null;
};

export type SustainabilityRoadmapCreateRequest = {
  account_id: number;
  baseline_year: number;
  target_year: number;
  roadmap_document_url?: string | null;
  priority_areas: SustainabilityRoadmapAreaPayload[];
};

export const submitSustainabilityRoadmap = async (
  payload: SustainabilityRoadmapCreateRequest,
  file?: File | null,
) => {
  try {
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));

    if (file) {
      formData.append("file", file);
    }

    const data: any = await axiosInstance.post(
      `/partner/sustainability-roadmap`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return {
      status: "success" as const,
      message: "Roadmap submitted successfully",
      data,
    };
  } catch (err: any) {
    return {
      status: "error" as const,
      message:
        err?.response?.data?.errors ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to submit sustainability roadmap",
    };
  }
};

export const getSustainabilityRoadmap = async (accountId: number) => {
  try {
    const res: any = await axiosInstance.get(
      `/partner/sustainability-roadmap`,
      {
        params: { accountId },
      },
    );

    const payload = res?.data?.data ?? res?.data ?? res;

    return {
      status: "success" as const,
      data: payload,
    };
  } catch (err: any) {
    return {
      status: "error" as const,
      message:
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to fetch sustainability roadmap",
    };
  }
};
export const deleteSummaryMetric = async (id: number) => {
  try {
    const response = await axiosInstance.delete(
      `/coordinator/summary-metrics/${id}`,
    );

    return {
      status: "success",
      data: response.data,
    };
  } catch (error: any) {
    return {
      status: "error",
      message:
        error?.response?.data?.errors ||
        error?.response?.data?.message ||
        "Failed to delete summary metric",
    };
  }
};
